import { AzureAvatarService } from "@service/avatar/azure-avatar.service";
import { useDispatch } from "react-redux";
import { useSendAudioMessageMutation } from "@store/companionApi";
import {
  setProcessing,
  setStreamState,
  setTalking,
  setText,
} from "@store/avatarSlice";
import { StreamState } from "@service/companion-streaming.service";
import splitTextToSentences from "@utils/splitTextToSentences";
import { MutableRefObject, useCallback } from "react";
import { EventType } from '@models/audio-message';
import { ConversationPacing } from "@models/conversation-settings";

const MAX_SENTENCE_LENGTH = 500;

const useBatching = ({
  sessionId,
  avatar,
  ttsInterruptGenerationRef,
}: {
  sessionId: string;
  avatar: MutableRefObject<AzureAvatarService | null>;
  ttsInterruptGenerationRef?: MutableRefObject<number>;
  conversationPacing?: ConversationPacing;
}) => {
  const dispatch = useDispatch();
  const [sendAudioMessage] = useSendAudioMessageMutation();

  const speakSentence = useCallback(
    async (sentence: string) => {
      if (!avatar.current?.running) {
        return;
      }
      await avatar.current.speak(sentence);
    },
    [avatar],
  );

  const speakSentences = useCallback(
    async (sentences: string[]) => {
      const genAtStart = ttsInterruptGenerationRef?.current ?? 0;
      dispatch(setTalking(true));
      try {
        for (const sentence of sentences) {
          if (
            ttsInterruptGenerationRef &&
            ttsInterruptGenerationRef.current !== genAtStart
          ) {
            break;
          }
          await speakSentence(sentence);
        }
      } catch (e) {
        console.error(e);
      } finally {
        dispatch(setTalking(false));
      }
    },
    [dispatch, speakSentence, ttsInterruptGenerationRef],
  );

  const sendMessage = useCallback(
    async (
      audioBlob?: Blob,
      photoId?: string,
      eventType?: EventType,
      conversationPacing?: ConversationPacing,
    ): Promise<void> => {
      dispatch(setProcessing(true));
      dispatch(setStreamState(StreamState.Processing));
      try {
        const { data } = await sendAudioMessage({
          audio: audioBlob || null,
          sessionId,
          photoId: photoId || null,
          eventType: eventType || null,
          conversationPacing,
        });

        if (!data) {
          return;
        }

        const { aiMessage } = data;

        if (!aiMessage) {
          return;
        }
        dispatch(setText(aiMessage));
        const sentences = splitTextToSentences(aiMessage, MAX_SENTENCE_LENGTH);

        dispatch(setStreamState(StreamState.Streaming));
        await speakSentences(sentences);
      } catch (e) {
        console.error(e);
      } finally {
        dispatch(setProcessing(false));
        dispatch(setStreamState(null));
      }
    },
    [dispatch, sendAudioMessage, sessionId, speakSentences],
  );

  return sendMessage;
};

export default useBatching;
